import { useEffect, useState } from 'react';
import { X, AlertTriangle, AlertCircle, Calendar } from 'lucide-react';
import { agedStockApi } from '../../services/agedStockApi';
import { InventoryComments } from '../inventory/InventoryComments';
import type { AgedStockItemDetail, LotFlagType } from '../../types/agedStock';

interface LotDetailDrawerProps {
    itemId: string;
    lotCode: string;
    onClose: () => void;
    canEdit: boolean;
    onToggleFlag: (item: AgedStockItemDetail, flagType: LotFlagType) => void;
    onCommentAdded: () => void;
    refreshKey: number;
}

function formatDate(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-CH');
}

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value);
}

export function LotDetailDrawer({
    itemId, lotCode, onClose, canEdit, onToggleFlag, onCommentAdded, refreshKey,
}: LotDetailDrawerProps) {
    const [detail, setDetail] = useState<AgedStockItemDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        agedStockApi.get(itemId, lotCode)
            .then(d => { if (!cancelled) setDetail(d); })
            .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load lot detail'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [itemId, lotCode, refreshKey]);

    return (
        <aside className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-y-auto sticky top-0 self-start max-h-[calc(100vh-120px)] w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sticky top-0 bg-white z-10">
                <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-gray-900 truncate" title={detail?.ProductName ?? ''}>
                        {detail?.ProductName ?? itemId}
                    </h2>
                    <p className="text-xs text-gray-500">
                        {detail?.ProductCode} · Lot <code className="rounded bg-gray-100 px-1">{lotCode}</code> · WH {detail?.WarehouseCode}
                    </p>
                </div>
                <button onClick={onClose} className="ml-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="px-4 py-3 space-y-4">
                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    </div>
                )}
                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
                )}

                {detail && (
                    <>
                        <section className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <p className="text-xs uppercase text-gray-500">First Purchase</p>
                                <p className="font-medium text-gray-900">{formatDate(detail.FirstPurchaseDate)}</p>
                                <p className="text-xs text-gray-500">{detail.AgeDays != null ? `${detail.AgeDays} days old` : ''}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-gray-500">Stock Qty</p>
                                <p className="font-medium text-gray-900">{formatNumber(detail.CurrentStockQty, 2)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-gray-500">Stock Value EUR</p>
                                <p className="font-medium text-gray-900">{formatNumber(detail.CurrentStockValueEUR)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase text-gray-500">Latest Sale</p>
                                <p className="font-medium text-gray-900">{formatDate(detail.LatestSalesDate)}</p>
                                {detail.LatestSaleCustomerName && (
                                    <p className="font-medium text-gray-900 truncate" title={detail.LatestSaleCustomerName}>
                                        {detail.LatestSaleCustomerName}
                                    </p>
                                )}
                                {detail.LatestSaleSalesManagerName && (
                                    <p className="text-gray-700 truncate" title={detail.LatestSaleSalesManagerName}>
                                        SM: {detail.LatestSaleSalesManagerName}
                                    </p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase text-gray-500">Vendor</p>
                                <p className="font-medium text-gray-900 truncate" title={detail.VendorID ?? ''}>
                                    {detail.VendorName ?? '—'}
                                </p>
                                {detail.SupplierName && detail.SupplierName !== detail.VendorName && (
                                    <p className="text-[10px] text-gray-400 truncate" title={detail.SupplierID ?? ''}>
                                        Master supplier: {detail.SupplierName}
                                    </p>
                                )}
                            </div>
                            <div>
                                <p className="text-xs uppercase text-gray-500">Category</p>
                                <p className="font-medium text-gray-900 truncate">
                                    {detail.CategoryName ?? '—'}
                                </p>
                            </div>
                        </section>

                        {detail.HasExpiryData && (
                            <section className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                <p className="flex items-center gap-1 text-xs text-gray-700">
                                    <Calendar className="h-3.5 w-3.5" />
                                    System Expiry Data:
                                    <span className="font-medium">{formatDate(detail.ExpirationDate)}</span>
                                    {detail.IsExpired && (
                                        <span className="rounded bg-red-100 px-1 py-0.5 text-red-700 font-semibold">EXPIRED</span>
                                    )}
                                    {detail.DaysToExpiry != null && !detail.IsExpired && detail.DaysToExpiry < 90 && (
                                        <span className="rounded bg-yellow-100 px-1 py-0.5 text-yellow-800">
                                            in {detail.DaysToExpiry}d
                                        </span>
                                    )}
                                </p>
                                <p className="mt-1 text-[11px] text-gray-500 italic">
                                    System indicator from BC ItemTrackingEntries — not the same as a manual flag.
                                </p>
                            </section>
                        )}

                        <section className="flex flex-wrap gap-2">
                            <button
                                disabled={!canEdit}
                                onClick={() => onToggleFlag(detail, 'Expired')}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs disabled:opacity-50 ${
                                    detail.HasExpiredFlag
                                        ? 'border-red-300 bg-red-50 text-red-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-red-50'
                                }`}
                            >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {detail.HasExpiredFlag ? 'Expired flag set' : 'Flag as Expired'}
                            </button>
                            <button
                                disabled={!canEdit}
                                onClick={() => onToggleFlag(detail, 'QualityIssue')}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs disabled:opacity-50 ${
                                    detail.HasQualityIssueFlag
                                        ? 'border-purple-300 bg-purple-50 text-purple-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-purple-50'
                                }`}
                            >
                                <AlertCircle className="h-3.5 w-3.5" />
                                {detail.HasQualityIssueFlag ? 'Quality flag set' : 'Flag Quality Issue'}
                            </button>
                        </section>

                        <section className="border-t border-gray-100 pt-3">
                            <InventoryComments
                                itemId={itemId}
                                comments={detail.comments}
                                canEdit={canEdit}
                                onCommentAdded={onCommentAdded}
                                lotCode={lotCode}
                            />
                        </section>
                    </>
                )}
            </div>
        </aside>
    );
}
